/**
 * 契約者コントロールパネルの起動点。
 *
 * Provider の順序に意味がある:
 *   ThemeProvider（data-theme を先に確定）
 *     → QueryClientProvider（以降のフックがクエリを使える）
 *       → TooltipProvider（用語の補足ツールチップ）
 *         → ToastProvider（mutation の結果通知）
 *           → RouterProvider
 */
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider, ToastProvider, TooltipProvider } from '@mail-sv/ui';
import { ApiError } from './lib/api.js';
import { router } from './router.js';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 4xx は再試行しても結果が変わらない（権限・入力・上限）。5xx とネットワークだけ再試行する。
      retry: (count, error) =>
        error instanceof ApiError && error.status >= 400 && error.status < 500 ? false : count < 2,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 30_000,
    },
    mutations: { retry: 0 },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root が見つかりません');

createRoot(container).render(
  <React.StrictMode>
    {/* 契約者パネルの既定はライト。index.html 側でも data-theme="light" を先に当てている。 */}
    <ThemeProvider defaultTheme="light" storageKey="mail-sv.theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={200}>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
